import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Button,
  Typography,
  Grid,
  TextField,
  Stack,
  Avatar,
  IconButton,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import * as yup from "yup";
import TextFields from "../../../Component/form/TextFields";
import SingleSelect from "../../../Component/form/SingleSelect";
import CloseIcon from "@mui/icons-material/Close";
import FilePreviewDialog from "../../../Component/commen/FilePreviewDialog";
import { useDropzone } from "react-dropzone";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CustomAmountInput from "../../../helper/Form/Amount";
import { projectTypeOptionsDevelopment } from "../../../config/Main";
const validationSchema = yup.object().shape({
  projectName: yup.string().required("Project name is required"),
  projectType: yup.string().required("Please select a project type"),
  amountRequested: yup
    .number()
    .required("Amount is required")
    .positive("Amount must be positive"),
  projectDescription: yup.string().required("Project description is required"),
});

// Project type options

export default function ProjectDetails({
  handleNext,
  getValues,
  setGetValues,
  handleBack,
  setFiles,
  files,
}) {
  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      projectName: getValues?.projectDetails?.projectName || "",
      projectType: getValues?.projectDetails?.projectType || "",
      amountRequested: getValues?.projectDetails?.amountRequested || "",
      projectDescription: getValues?.projectDetails?.projectDescription || "",
    },
  });

  const [previewFile, setPreviewFile] = useState(null);
  const [openPreview, setOpenPreview] = useState(false);
  const onDrop = useCallback((acceptedFiles) => {
    setFiles((prevFiles) => [...prevFiles, ...acceptedFiles]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/*": [
        ".jpeg",
        ".jpg",
        ".png",
        ".gif",
        ".pdf",
        ".docx",
        ".xlsx",
        ".xls",
        ".pptx",
        ".ppt",
      ],
    },
    maxFiles: 5,
  });
  const removeFile = (index) => {
    setFiles(files.filter((_, i) => i !== index));
  };
  useEffect(() => {
    if (getValues) {
      setValue("projectName", getValues.projectDetails?.projectName || "");
      setValue("projectType", getValues.projectDetails?.projectType || "");
      setValue(
        "amountRequested",
        getValues.projectDetails?.amountRequested || ""
      );
      setValue(
        "projectDescription",
        getValues.projectDetails?.projectDescription || ""
      );
    }
  }, [getValues, setValue]);

  const onSubmit = (data) => {
    setGetValues((prevData) => ({
      ...prevData,
      projectDetails: data,
    }));

    handleNext();
  };

  return (
    <Box sx={{ mx: "auto", maxWidth: 800 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <TextFields
            control={control}
            name="projectName"
            label="Project Name"
            placeholder="Enter Project Name"
            error={errors.projectName}
            required={true}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <SingleSelect
            control={control}
            name="projectType"
            label="Project Type"
            error={errors.projectType}
            data={projectTypeOptionsDevelopment}
            required={true}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <CustomAmountInput
            label="Amount Requested"
            name="amountRequested"
            placeholder="Enter Amount"
            control={control}
            error={errors.amount}
            className="amountInput"
          />
        </Grid>

        <Grid item xs={6}>
          <Controller
            name="projectDescription"
            control={control}
            render={({ field }) => (
              <TextField
                name="projectDescription"
                label="Project Description"
                placeholder="Enter Project Description"
                id="expenses-description-textarea"
                required
                fullWidth
                multiline
                rows={4}
                {...field}
                error={errors.projectDescription}
                helperText={errors.description?.message}
              />
            )}
          />
        </Grid>
        <Grid item xs={6}>
          {/* File Dropzone */}
          <Box sx={{ mb: 2 }}>
            <Box
              {...getRootProps()}
              sx={{
                border: "2px dashed",
                borderColor: isDragActive ? "primary.main" : "primary.main",
                borderRadius: 1,
                p: 4.3,
                textAlign: "center",
                cursor: "pointer",
                backgroundColor: isDragActive ? "action.hover" : "#e8f5e9",
              }}
            >
              <input {...getInputProps()} />
              {isDragActive ? (
                <Typography>Drop the files here ...</Typography>
              ) : (
                <Typography>
                  Drag & drop files here, or click to select files
                </Typography>
              )}
              <Typography variant="caption" color="text.secondary">
                (Supports images and pdf xl max 5 files)
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12}>
          {/* File Previews */}
          {files.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{ textAlign: "left" }}
              >
                Selected Files:
              </Typography>
              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                {files.map((file, index) => {
                  const fileName =
                    file.name.length > 10
                      ? `${file.name.substring(0, 7)}...${file.name
                          .split(".")
                          .pop()}`
                      : file.name;

                  return (
                    <Box
                      key={index}
                      sx={{
                        position: "relative",
                        width: 100,
                        height: 120,
                        mb: 1,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <Box
                        onClick={() => {
                          if (file.type.startsWith("image/")) {
                            setPreviewFile(file);
                            setOpenPreview(true);
                          }
                        }}
                        sx={{
                          cursor: "pointer",
                          width: 100,
                          height: 100,
                          position: "relative",
                          "&:hover": {
                            opacity: 0.8,
                          },
                        }}
                      >
                        {file.type.startsWith("image/") ? (
                          <Avatar
                            src={URL.createObjectURL(file)}
                            variant="rounded"
                            sx={{
                              width: "100%",
                              height: "100%",
                            }}
                          />
                        ) : (
                          <Avatar
                            variant="rounded"
                            sx={{
                              width: "100%",
                              height: "100%",
                              bgcolor: "grey.200",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <InsertDriveFileIcon color="action" />
                          </Avatar>
                        )}
                      </Box>

                      <Typography
                        variant="caption"
                        sx={{
                          mt: 0.5,
                          textAlign: "center",
                          width: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {fileName}
                      </Typography>

                      <IconButton
                        size="small"
                        sx={{
                          position: "absolute",
                          top: -8,
                          right: 0,
                          backgroundColor: "error.light",
                          color: "white",
                          "&:hover": {
                            backgroundColor: "error.light",
                          },
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFile(index);
                        }}
                      >
                        <CloseIcon fontSize="small" sx={{ fontSize: "14px" }} />
                      </IconButton>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          )}
        </Grid>
      </Grid>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mt: 3,
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
        }}
      >
        <Button
          variant="outlined"
          onClick={handleBack}
          sx={{ textTransform: "none" }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          sx={{ textTransform: "none" }}
          onClick={handleSubmit(onSubmit)}
        >
          Next
        </Button>
      </Box>
      <FilePreviewDialog
        open={openPreview}
        onClose={() => setOpenPreview(false)}
        file={previewFile}
      />
    </Box>
  );
}
